import {
    addDays,
    addHours,
    compareAsc,
    format,
    intlFormat,
    isValid,
    isWeekend,
    startOfDay,
} from 'date-fns'
import { useTranslation } from 'react-i18next'
import { getDateFnsLocale, getLocale } from './locale'

const HHmm = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
}

const ddMM = {
    day: '2-digit',
    month: '2-digit',
}

const dMMM = {
    day: 'numeric',
    month: 'short',
}

interface OptionsMap {
    [key: string]: {
        [key: string]: string | boolean
    }
}

export const optionsMap: OptionsMap = {
    'dd-MM': { ...ddMM },
    'dd-MM HH:mm': { ...ddMM, ...HHmm },
    'dd-MM-yy': { ...ddMM, year: '2-digit' },
    'dd-MM-yy HH:mm': { ...ddMM, year: '2-digit', ...HHmm },
    'dd-MM-yyyy': { ...ddMM, year: 'numeric' },
    'dd-MM-yyyy HH:mm': { ...ddMM, year: 'numeric', ...HHmm },
    'dd-MM-yyyy HH:mm:ss': {
        ...ddMM,
        year: 'numeric',
        ...HHmm,
        second: '2-digit',
    },
    'd MMM': { ...dMMM },
    'd MMM HH:mm': { ...dMMM, ...HHmm },
    'd MMM yyyy': { ...dMMM, year: 'numeric' },
    'd MMM yyyy HH:mm': { ...dMMM, year: 'numeric', ...HHmm },
    'd MMMM': { day: 'numeric', month: 'long' },
    'd MMMM HH:mm': { day: 'numeric', month: 'long', ...HHmm },
    'd MMMM yyyy': { day: 'numeric', month: 'long', year: 'numeric' },
    'd MMMM yyyy HH:mm': { day: 'numeric', month: 'long', year: 'numeric', ...HHmm },
    'EEEE d MMMM HH:mm': { weekday: 'long', day: 'numeric', month: 'long', ...HHmm },
    'EEEE d MMMM yyyy HH:mm': {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...HHmm,
    },
}

/** @deprecated Use getDate from @trustoo/funcs/date instead */
export const getDate = (string = '') => new Date(string.replace(' ', 'T'))

// test for DB UTC datetime string: "2021-04-02 09:10:34" or "2021-04-02T09:10:34+00:00" from Cake
const isFromDatabase = (dateTime: string) =>
    /(\d{4})-(\d{1,2})-(\d{1,2})(T|\s)(\d{2}):(\d{2}):(\d{2})/.test(dateTime)

export const getDateObject = (dateTime: string | Date | undefined | null) => {
    // date is undefined
    if (!dateTime) {
        return new Date()
    }

    // date is already a Date object
    if (dateTime instanceof Date) {
        return dateTime
    }

    // date is string
    return isFromDatabase(dateTime)
        ? new Date(dateTime.replace(' ', 'T') + (dateTime.indexOf('+00:00') === -1 ? '+00:00' : ''))
        : new Date(dateTime)
}

export const getDateObjectWithResettedTime = (dateTime: string | Date | undefined) => {
    const dateObject = getDateObject(dateTime)
    dateObject.setHours(0, 0, 0, 0)
    return dateObject
}

/**
 * Convert a datetime string or a Date object to a localized datetime string
 *
 * Important:
 * In Germany dots are used in date formats string: 21.10.2021 and 21. Oktober 2021
 * Date-fns itself has only 4 predefined variations for localized dateFormats (full, long, medium and short i.e. PPPP, PPP, PP, P)
 * For other formats use the Intl.DateTimeFormat to convert
 * See for examples: /pro/dev/dateFormat
 */
export const getDateTimeString = (
    dateTime: string | Date | undefined | null,
    formatString: string = 'dd-MM-yy HH:mm',
): string => {
    try {
        const dateObject = getDateObject(dateTime)
        if (!isValid(dateObject)) {
            throw new Error(`Invalid date: ${dateObject.getDate()}`)
        }

        if (formatString.includes('d')) {
            // use intFormat map
            const optionsObject = optionsMap[formatString]
            if (!optionsObject || Object.keys(optionsObject).length === 0) {
                throw new Error(`Invalid date format: ${formatString}`)
            }
            return intlFormat(dateObject, optionsObject, { locale: getLocale() })
        } else {
            // use date fns format
            return format(dateObject, formatString, { locale: getDateFnsLocale() })
        }
    } catch (e) {
        console.error(e)
        // don't crash the entire app
        return 'INVALID DATE'
    }
}

// day-only versions
export const compareAscDay = (date1: string | Date, date2: string | Date) =>
    compareAsc(startOfDay(getDateObject(date1)), startOfDay(getDateObject(date2)))

export const isPastDay = (date: string | Date) => compareAscDay(date, new Date()) === -1

export const isFutureDay = (date: string | Date) => compareAscDay(date, new Date()) === 1

export const useFormatDays = () => {
    const { t } = useTranslation('shared')
    const formatDays = (days: number) => (days === 1 ? t('format_day') : t('format_days', { days }))
    return { formatDays }
}

export const HOUR_SEC = 3600
export const DAY_SEC = 24 * HOUR_SEC

export const isOutsideWorkingHours = (dt: Date): boolean =>
    isWeekend(dt) || dt < addHours(startOfDay(dt), 9) || addHours(startOfDay(dt), 17) < dt

// in our timezone
export const diffWorkdaySeconds = (
    dt1: string | Date,
    dt2: string | Date,
    isExcludedHour: (dt: Date) => boolean = isWeekend,
): number => {
    // noinspection DuplicatedCode
    dt1 = getDateObject(dt1)
    dt2 = getDateObject(dt2)
    let factor = 1

    if (dt1 > dt2) {
        const temp = dt1
        dt1 = dt2
        dt2 = temp
        factor = -1
    }

    // using the point half-way the hour makes it less sensitive to edge cases in the 'isExcludedHour' function
    const dt1DayHour = new Date(dt1)
    dt1DayHour.setMinutes(30, 0, 0)

    const dt2DayHour = new Date(dt2)
    dt2DayHour.setMinutes(30, 0, 0)

    let diffWorkdayHours = 0

    for (let currentDt = dt1DayHour; currentDt < dt2DayHour; currentDt = addHours(currentDt, 1)) {
        if (!isExcludedHour(currentDt)) {
            diffWorkdayHours++
        }
    }

    const getSec = (dt: Date): number =>
        isExcludedHour(dt) ? 0 : dt.getMinutes() * 60 + dt.getSeconds()
    return factor * (diffWorkdayHours * HOUR_SEC - getSec(dt1) + getSec(dt2))
}

export const wholeBusinessDaysBetween = (dt1: string | Date, dt2: string | Date): number => {
    // noinspection DuplicatedCode
    dt1 = getDateObject(dt1)
    dt2 = getDateObject(dt2)
    let factor = 1

    if (dt1 > dt2) {
        const temp = dt1
        dt1 = dt2
        dt2 = temp
        factor = -1
    }

    const dt1Start = new Date(dt1)
    dt1Start.setHours(0, 0, 0, 0)

    const dt2Start = new Date(dt2)
    dt2Start.setHours(0, 0, 0, 0)

    let days = 0

    for (
        let currentDt = addDays(dt1Start, 1);
        currentDt < dt2Start;
        currentDt = addDays(currentDt, 1)
    ) {
        if (!isWeekend(currentDt)) {
            days++
        }
    }

    return factor * days
}
